import * as React from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { appTheme } from './themes/theme';
import store, { persistor } from './store';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider } from 'react-redux';
import { SearchBarContainerContext } from 'components/AppBar/AppBar';

// Routes
import ProtectedRoute from './routes/protectedRoute';
import AuthRoute from './routes/AuthRoute';
import ErrorPage from './routes/errorPage';
import Login from './routes/login';
import Home from './routes/home';
import UsersList from './pages/users/list/UsersList';
import UserPage from './pages/users/page/UserPage';
import EntitiesPage from './pages/entities/EntitiesPage';
import VehiclesLayout from './pages/vehicles/list/VehiclesLayout';
import VehiclesList from './pages/vehicles/list/VehiclesList';
import VehiclePage from './pages/vehicles/page/VehiclePage';
import PublicVehiclePage from './pages/vehicles/public/PublicVehiclePage';
import VinDecoderPage from './pages/vinDecoder/VinDecoderPage';
import SpeedInsightsWithRoute from './components/SpeedInsights/SpeedInsightsWithRoute';
import EnvironmentBanner from './components/EnvironmentBanner/EnvironmentBanner';

// Initialize a React query client
const queryClient = new QueryClient();

// Wraps every route so SpeedInsightsWithRoute renders inside RouterProvider and can read the matched route pattern via useMatches.
function RootLayout() {
  return (
    <>
      <SpeedInsightsWithRoute />
      <Outlet />
    </>
  );
}

// Initialize react router
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/',
        element: <ProtectedRoute />,
        errorElement: <ErrorPage />,
      },
      {
        path: '/login',
        element: <Login />,
      },
      {
        path: '/home',
        element: <Home />,
        children: [
          {
            path: 'vehicles',
            element: <VehiclesLayout />,
            children: [
              {
                element: <AuthRoute requireSuperuser allowWarehouseUser />,
                children: [
                  {
                    index: true,
                    element: <VehiclesList />,
                  },
                ],
              },
              {
                path: ':id',
                element: <AuthRoute />,
                children: [
                  {
                    index: true,
                    element: <VehiclePage />,
                  },
                ],
              },
            ],
          },
          {
            path: 'users',
            element: <AuthRoute requireSuperuser />,
            children: [
              {
                index: true,
                element: <UsersList />,
              },
            ],
          },
          {
            path: 'users/:id',
            element: <AuthRoute />,
            children: [
              {
                index: true,
                element: <UserPage />,
              },
            ],
          },
          {
            path: 'entities',
            element: <AuthRoute requireSuperuser />,
            children: [
              {
                index: true,
                element: <EntitiesPage />,
              },
            ],
          },
          {
            path: 'vin-decoder',
            element: <AuthRoute requireSuperuser />,
            children: [
              {
                index: true,
                element: <VinDecoderPage />,
              },
            ],
          },
        ],
      },
      {
        path: '/public/vehicles/:id',
        element: <PublicVehiclePage />,
        errorElement: <ErrorPage />,
      },
    ],
  },
]);

function App() {
  const [searchBarContainer, setSearchBarContainer] = React.useState<HTMLDivElement | null>(null);
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate persistor={persistor}>
          <ThemeProvider theme={appTheme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <SearchBarContainerContext.Provider
                value={{ container: searchBarContainer, setContainer: setSearchBarContainer }}
              >
                <CssBaseline enableColorScheme />
                <EnvironmentBanner>
                  <RouterProvider router={router} />
                </EnvironmentBanner>
              </SearchBarContainerContext.Provider>
            </LocalizationProvider>
          </ThemeProvider>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
